const { getElement, getElements } = window.BAO.Utils

class CollectionFiltersRenderer {
  /**
   * @param {CollectionFiltersForm} form
   */
  constructor (form) {
    /**
     * @type {CollectionFiltersForm}
     * @public
     */
    this.form = form

    /**
     * @type {CollectionFiltersPaginationRender}
     * @public
     */
    this.paginationRenderer = new CollectionFiltersPaginationRender(this)
  }

  /**
   * @param {Function} filterDataUrl
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  fromCache (filterDataUrl, e) {
    const html = this.form.filterData.find(element => filterDataUrl(element))
      .html

    this.form.log('fromCache', { filterDataUrl, e, html })

    this.render(html, e)
  }

  /**
   * @param {String} section
   * @param {String} url
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  async fromFetch (section, url, e) {
    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)

    this.form.log('fromFetch', { section, url, html })

    this.form.filterData.push({ html, url })

    this.render(html, e)
  }

  /**
   * @param {String} html
   * @param {SubmitEvent|PointerEvent} e
   *
   * @returns {void}
   */
  render (html, e) {
    this.renderFilters(html, e)
    this.renderProductGridContainer(html, e)
    this.paginationRenderer.render(html, e)
    this.renderProductCount(html)

    this.form.loading = false
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  renderFilters (html, e) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')
    const detailSelector = this.form.els.facet.selector

    const facetDetailElements = getElements(detailSelector, {
      context: parsedHTML,
    })

    const facetLabelElements = getElements(
      this.form.els['facet-label'].selector,
      {
        context: parsedHTML,
      },
    )

    /**
     * @param {HTMLElement} element
     *
     * @returns {Boolean}
     */
    const matchesIndex = element => {
      const facet = e ? e.target.closest(detailSelector) : null

      return facet ? element.dataset.index === facet.dataset.index : false
    }
    const facetsToRender = facetDetailElements.filter(el => !matchesIndex(el))

    this.form.log('renderFilters', {
      e,
      parsedHTML,
      facetDetailElements,
      facetLabelElements,
    })

    facetsToRender.forEach(element => {
      const selector = `${detailSelector}[data-index="${element.dataset.index}"]`

      const el = getElement(selector)

      el.innerHTML = element.innerHTML
    })

    facetLabelElements.forEach(element => {
      const selector = `${this.form.els['facet-label'].selector}[data-index="${element.dataset.index}"]`

      const el = getElement(selector)

      el.innerHTML = element.innerHTML
    })

    this.renderActiveFacets(parsedHTML)
    this.renderAdditionalElements(parsedHTML)
  }

  /**
   * @param {Document} parsedHTML
   *
   * @returns {void}
   */
  renderActiveFacets (parsedHTML) {
    this.activeFacetSelectors.forEach(selector => {
      const activeFacetElement = parsedHTML.querySelector(selector)

      this.form.log('renderActiveFacets', {
        parsedHTML,
        activeFacetElement,
        selector,
      })

      if (!activeFacetElement) return

      getElements(selector).forEach(
        el => (el.innerHTML = activeFacetElement.innerHTML),
      )
    })
  }

  /**
   * @param {Document} parsedHTML
   *
   * @returns {void}
   */
  renderAdditionalElements (parsedHTML) {
    const newFiltersToggle = parsedHTML.querySelector(
      '[data-collection-filters-form-external-el="filters-toggle"]',
    )
    /** @type {HTMLElement} */
    const filtersToggle = this.form.externalEls['filters-toggle'].element

    filtersToggle.replaceWith(newFiltersToggle)
  }

  /**
   * @param {String} html
   * @param {PointerEvent} e
   *
   * @returns {void}
   */
  renderProductGridContainer (html, e) {
    const direction = this.getPaginationDirection(e)
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')

    const newItems = document.createDocumentFragment()
    const newElements = getElements(
      '[data-collection-filters-form-external-el="item"]',
      {
        context: parsedHTML,
      },
    )

    const itemContainer = this.form.externalEls['item-container'].element
    const productGrid = this.form.externalEls['product-grid'].element

    if (newElements.length > 0) {
      const preservedElement = itemContainer.querySelector(
        false,
        // '[data-filter-preserve]',
      )
      newElements.forEach((el, index) => {
        if (
          preservedElement &&
          index === parseInt(preservedElement.getAttribute('data-position'))
        ) {
          newItems.appendChild(preservedElement)
        }
        newItems.appendChild(el)
      })

      const newCurrentPage = parsedHTML
        .querySelector(
          '[data-collection-filters-form-external-el="product-grid"]',
        )
        .getAttribute('data-current-page')
      productGrid.setAttribute('data-current-page', newCurrentPage)
      itemContainer.setAttribute('data-page', newCurrentPage)

      const newLastPage = parsedHTML
        .querySelector(
          '[data-collection-filters-form-external-el="product-grid"]',
        )
        .getAttribute('data-last-page')
      productGrid.setAttribute('data-last-page', newLastPage)

      if (direction == null) {
        itemContainer.innerHTML = ''
        itemContainer.append(newItems)
      } else if (direction === 'PREVIOUS') {
        itemContainer.prepend(newItems)
      } else if (direction === 'NEXT') {
        itemContainer.append(newItems)
      }

      window.yotpoWidgetsContainer.initWidgets(false)

      /* Fix for ajax rendered blurry images in safari */
      getElements(
        `[data-collection-filters-form-external-el="item"][data-page="${newCurrentPage}"] img[srcset]`,
        {
          context: itemContainer,
        },
      )?.forEach(imageEl => {
        // eslint-disable-next-line no-self-assign
        imageEl.outerHTML = imageEl.outerHTML
      })
    } else {
      const noProductsToDisplayText = getElements('[data-product-grid-empty]', {
        context: parsedHTML,
      })

      itemContainer.innerHTML = ''

      itemContainer.append(noProductsToDisplayText[0])
    }
  }

  /**
   * @param {String} html
   *
   * @returns {void}
   */
  renderProductCount (html) {
    const count = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(this.form?.externalEls['product-count']?.selector)
      ?.innerHTML
    this.form.log('renderProductCount', {
      html,
      count,
      countElements: this.countElements,
    })
    if (!count) return

    this.countElements.forEach(element => (element.innerHTML = count))
  }

  /**
   * @param {PointerEvent} e
   *
   * @return 'NEXT'|'PREVIOUS'|null
   */
  getPaginationDirection (e) {
    const paginationElement = e?.target?.closest(
      `${this.form.externalEls['load-previous']?.selector}, ${this.form.externalEls['load-more']?.selector}`,
    )
    if (!paginationElement) return null

    return paginationElement === this.form.externalEls['load-previous']?.element
      ? 'PREVIOUS'
      : 'NEXT'
  }

  /**
   * @returns {Element[]}
   */
  get countElements () {
    return getElements('[data-product-grid-count]')
  }

  /**
   * @returns {String[]}
   */
  get activeFacetSelectors () {
    if ('active-facets' in this.form.externalEls) {
      return [this.form.externalEls['active-facets'].selector]
    }

    return [this.form.els['active-facets'].selector]
  }
}

class CollectionFiltersPaginationRender {
  constructor (renderer) {
    /**
     * @type {CollectionFiltersRenderer}
     * @public
     */
    this.renderer = renderer

    this.currentLowestPage =
      Number(
        this.form.externalEls['product-grid'].element.dataset.currentPage,
      ) || 1
    this.currentHighestPage = this.currentLowestPage
  }

  /**
   * @param {string} html
   */
  render (html, e) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')

    const paginationElement = e?.target?.closest(
      `${this.form.externalEls['load-previous']?.selector}, ${this.form.externalEls['load-more']?.selector}`,
    )
    if (!paginationElement) {
      this.currentLowestPage =
        Number(
          this.form.externalEls['product-grid'].element.dataset.currentPage,
        ) || 1
      this.currentHighestPage = this.currentLowestPage
    }

    this.renderLoadPrevious(parsedHTML)
    this.renderLoadMore(parsedHTML)

    if (this.currentPage < this.currentLowestPage) {
      this.currentLowestPage = this.currentPage
    }

    if (this.currentPage > this.currentHighestPage) {
      this.currentHighestPage = this.currentPage
    }
  }

  /**
   * @param {Document} html
   */
  renderLoadPrevious (html) {
    const loadPreviousContainer = this.form.externalEls['load-previous']
      ?.element
    if (!this.shouldShowLoadPrevious) {
      if (loadPreviousContainer) {
        loadPreviousContainer.innerHTML = ''
      }
      return
    }
    if (!this.shouldRerenderLoadPrevious) return

    this._renderEl('load-previous', html)
  }

  /**
   * @param {Document} html
   */
  renderLoadMore (html) {
    const loadMoreContainer = this.form.externalEls['load-more']?.element
    if (!this.shouldShowLoadMore) {
      if (loadMoreContainer) {
        loadMoreContainer.innerHTML = ''
      }
      return
    }
    if (!this.shouldRerenderLoadMore) return

    this._renderEl('load-more', html)
  }

  /**
   * @param {string} section
   * @param {Document} html
   */
  _renderEl (section, html) {
    const sectionToRenderElement = this.form.externalEls[section]?.element
    if (sectionToRenderElement) {
      sectionToRenderElement.innerHTML = ''
      const newInnerHTML = getElement(this.form.externalEls[section].selector, {
        context: html,
      })?.innerHTML

      if (newInnerHTML) sectionToRenderElement.innerHTML = newInnerHTML
    }
  }

  get form () {
    return this.renderer.form
  }

  get shouldShowLoadPrevious () {
    const anElementFromTheFirstPageExists = !!getElement(
      `${this.form.externalEls.item?.selector}[data-page="1"]`,
    )
    if (anElementFromTheFirstPageExists) return false

    return this.currentPage > 1
  }

  get shouldShowLoadMore () {
    const anElementFromTheLastPageExists = getElement(
      `${this.form.externalEls.item?.selector}[data-page="${this.lastPage}"]`,
    )
    if (anElementFromTheLastPageExists) return false

    return Number(this.currentPage) < this.lastPage
  }

  get shouldRerenderLoadPrevious () {
    return this.currentPage < this.currentLowestPage
  }

  get shouldRerenderLoadMore () {
    return this.currentPage >= this.currentHighestPage
  }

  get currentPage () {
    const params = this.form.searchParams.current
    const page = params.get('page')

    return Number(page) || 1
  }

  /**
   * @returns {number}
   */
  get lastPage () {
    return Number(
      this.form.externalEls['product-grid'].element.dataset.lastPage,
    )
  }
}

/**
 * @typedef {Object} FilterData
 *
 * @property {string} html
 * @property {string} url
 */

export class CollectionFiltersForm extends window.BAO.CustomElement({
  SuperClass: HTMLFormElement,
}) {
  static get requiredExternalElements () {
    return ['container', 'product-grid']
  }

  isPaginating = false

  constructor () {
    super()

    /**
     * @type {Section[]}
     * @public
     */
    this.sections = [
      {
        id: 'template-collection-product-grid',
        section: this.dataset.id,
        selector: '.shopify-section',
      },
    ]

    /**
     * @type {FilterData[]}
     * @public
     */
    this.filterData = []

    /**
     * @type {{ previous: URLSearchParams, current: URLSearchParams, initial: URLSearchParams }}
     * @public
     */
    this.searchParams = {
      // eslint-disable-next-line compat/compat
      previous: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      current: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      initial: new URLSearchParams(window.location.search),
    }

    /**
     * @type {CollectionFiltersRenderer}
     * @public
     */
    this.renderer = new CollectionFiltersRenderer(this)
  }

  setupListeners () {
    super.setupListeners()

    this.onFacetRemovalClick = this.onFacetRemovalClick.bind(this)

    this.externalEls['sort-by'].elements.forEach(input => {
      this.listeners.add(input, 'input', this.onSortChange.bind(this))
    })
    this.listeners.add(this, 'input', this.onSubmit.bind(this))

    this.listeners.add(window, 'popstate', this.onHistoryChange.bind(this))

    this.listeners.add(document, 'click', this.onPaginationClick.bind(this))

    if (window.location.hash) {
      this.repositionToElement()
    }
  }

  /**
   * @param {InputEvent} e
   *
   * @returns {void}
   */
  onSortChange (e) {
    this.els['sort-by'].element.value = e.target.value

    this.onSubmit(e)
  }

  /**
   * @param {PopStateEvent} e
   *
   * @returns {void}
   */
  onHistoryChange (e) {
    const searchParams = e.state
      ? e.state.searchParams
      : this.form.searchParams.initial
    if (
      searchParams.toString() === this.form.searchParams.previous.toString()
    ) {
      return
    }

    this.form.renderPage({ searchParams, updateURLHash: false })
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onFacetRemovalClick (e) {
    e.preventDefault()

    const searchParams = e.currentTarget.href.includes('?')
      ? e.currentTarget.href.slice(e.currentTarget.href.indexOf('?') + 1)
      : ''

    this.log('onFacetRemovalClick', { searchParams })

    this.renderPage({ searchParams })
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onPaginationClick (e) {
    if (this.constructor.isPaginating) return

    if (!e.target.closest(this.externalEls?.pagination?.selector)) return

    this.constructor.isPaginating = true

    e.preventDefault()

    const anchor = e.target.closest('a')
    this.log('onPaginationClick', { anchor })
    if (!anchor || anchor.getAttribute('href') === '' || anchor.disabled) return

    const href = anchor.getAttribute('href')
    this.log('onPaginationClick', { href })
    // eslint-disable-next-line compat/compat
    const paginationSearchParams = new URLSearchParams(href.split('?')[1])
    const wantedPage = paginationSearchParams.get('page') ?? '1'
    // eslint-disable-next-line compat/compat
    const currentSearchParams = new URLSearchParams(
      this.searchParams.current.toString(),
    )
    currentSearchParams.set('page', wantedPage)

    this.renderPage({ searchParams: currentSearchParams.toString(), e })
  }

  repositionToElement () {
    const callback = () => {
      const el = getElement(window.location.hash)
      if (!el) return

      const { top } = el.getBoundingClientRect()
      const headerHeight = getElement(`#header`)?.offsetHeight || 0
      const offset = 20

      window.setTimeout(() => {
        window.scrollTo(0, window.scrollY + (top - headerHeight - offset))
      }, 100)
    }

    if (document.readyState === 'complete') {
      callback()
    } else {
      document.addEventListener('readystatechange', callback)
    }
  }

  /**
   * @param {InputEvent} e
   *
   * @returns {void}
   */
  onSubmit (e) {
    e.preventDefault()

    const formData = new FormData(this)
    // eslint-disable-next-line compat/compat
    const searchParams = new URLSearchParams(formData).toString()

    this.renderPage({ searchParams, e })
  }

  /**
   * @param {Object}
   * @param {String} searchParams
   * @param {InputEvent} [e]
   * @param {Boolean} [updateURLHash]
   *
   * @returns {void}
   */
  renderPage ({ searchParams, e, updateURLHash = true } = {}) {
    this.log('renderPage', { searchParams, e, updateURLHash })

    this.searchParams.previous = this.searchParams.current
    // eslint-disable-next-line compat/compat
    this.searchParams.current = new URLSearchParams(searchParams)

    this.loading = true

    this.sections.forEach(async section => {
      const url = `${window.location.pathname}?section_id=${
        section.section
      }&${this.searchParams.current.toString()}`
      /**
       * @param {FilterData} element
       *
       * @returns {Boolean}
       */
      const filterDataUrl = element => element.url === url

      this.log('renderPageSections', { section, url })

      this.filterData.some(element => filterDataUrl(element))
        ? this.renderer.fromCache(filterDataUrl, e)
        : this.renderer.fromFetch(section.section, url, e)
    })

    if (updateURLHash) this.updateURLHash(this.searchParams.current)
  }

  /**
   * @param {URLSearchParams} searchParams
   *
   * @returns {void}
   */
  updateURLHash (searchParams) {
    history.pushState(
      { searchParams: searchParams.toString() },
      '',
      `${window.location.pathname}${searchParams &&
        '?'.concat(searchParams.toString())}`,
    )
  }

  /**
   * @returns {Boolean}
   */
  get loading () {
    return this._loading
  }

  /**
   * @param {Boolean} val
   *
   * @returns {void}
   */
  set loading (val) {
    this._loading = val

    this.setAttribute('aria-busy', val.toString())

    this.externalEls?.container.element.setAttribute(
      'aria-busy',
      val.toString(),
    )

    if (val === false) {
      this.constructor.isPaginating = false
    }
  }
}

if (!customElements.get('collection-filters-form')) {
  customElements.define('collection-filters-form', CollectionFiltersForm, {
    extends: 'form',
  })
}

class PriceRange extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['min', 'max']
  }

  constructor () {
    super()

    this.setMinAndMaxValues()
  }

  setupListeners () {
    super.setupListeners()

    this.onRangeChange = this.onRangeChange.bind(this)

    Array.from([this.maxInput, this.minInput]).forEach(input => {
      this.listeners.add(input, 'change', this.onRangeChange)
    })
  }

  /**
   * @param {InputEvent} e
   *
   * @returns {void}
   */
  onRangeChange (e) {
    this.adjustToValidValues(e.target)
    this.setMinAndMaxValues()
  }

  setMinAndMaxValues () {
    if (this.maxInput.value) {
      this.minInput.setAttribute('max', this.maxInput.value)
    }
    if (this.minInput.value) {
      this.maxInput.setAttribute('min', this.minInput.value)
    }
    if (this.minInput.value === '') this.maxInput.setAttribute('min', '0')
    if (this.maxInput.value === '') {
      this.minInput.setAttribute('max', this.maxInput.getAttribute('max'))
    }
  }

  /**
   * @param {HTMLInputElement} input
   *
   * @returns {void}
   */
  adjustToValidValues (input) {
    const value = Number(input.value)
    const min = Number(input.getAttribute('min'))
    const max = Number(input.getAttribute('max'))

    if (value < min) input.value = min.toString()
    if (value > max) input.value = max.toString()
  }

  /**
   * @returns {HTMLInputElement}
   */
  get maxInput () {
    return this.els.max.element
  }

  /**
   * @returns {HTMLInputElement}
   */
  get minInput () {
    return this.els.min.element
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange)
}

class FacetRemove extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    this.listeners.add(this.querySelector('a'), 'click', e => {
      e.preventDefault()

      this.form.onFacetRemovalClick(e)
    })
  }

  get form () {
    const selector = 'collection-filters-form, [is="collection-filters-form"]'

    return this.closest(selector) || document.querySelector(selector)
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove)
}

class FilterFacet extends window.BAO.CustomElement({
  SuperClass: HTMLDetailsElement,
}) {
  static get requiredElements () {
    return ['summary', 'content']
  }

  constructor () {
    super()

    /**
     * @type {Animation|null}
     * @public
     */
    this.animation = null

    /**
     * @type {Boolean}
     * @public
     */
    this.isClosing = false

    /**
     * @type {Boolean}
     * @public
     */
    this.isExpanding = false
  }

  setupListeners () {
    super.setupListeners()

    this.onSummaryClick = this.onSummaryClick.bind(this)

    this.listeners.add(this, 'click', this.onSummaryClick)
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onSummaryClick (e) {
    if (!e.target.closest(this.els.summary.selector)) return

    e.preventDefault()

    if (this.isClosing || !this.open) {
      this.start()
    } else if (this.isExpanding || this.open) {
      this.close()
    }
  }

  start () {
    this.open = true

    window.requestAnimationFrame(() => this.expand())
  }

  close () {
    this.shrink()
  }

  shrink () {
    this.isClosing = true

    if (this.animation) {
      this.animation.cancel()
    }

    this.animation = this.content.animate(
      {
        opacity: [1, 0],
        transform: ['none', `translateY(-15px)`],
      },
      { duration: 400, easing: 'ease' },
    )

    this.animation.onfinish = () => this.onAnimationFinish(false)
    this.animation.oncancel = () => (this.isClosing = false)
  }

  expand () {
    this.isExpanding = true

    if (this.animation) {
      this.animation.cancel()
    }

    this.animation = this.content.animate(
      {
        opacity: [0, 1],
        transform: [`translateY(-15px)`, 'none'],
      },
      { duration: 400, easing: 'ease' },
    )

    this.animation.onfinish = () => this.onAnimationFinish(true)
    this.animation.oncancel = () => (this.isExpanding = false)
  }

  /**
   * @param {Boolean} open
   *
   * @returns {void}
   */
  onAnimationFinish (open) {
    this.open = open
    this.animation = null

    this.isClosing = false
    this.isExpanding = false
  }

  get content () {
    return window.BAO.Utils.getElement(this.els.content.selector, {
      context: this,
    })
  }
}

if (!customElements.get('filter-facet')) {
  customElements.define('filter-facet', FilterFacet, { extends: 'details' })
}

class LayoutSwitcher extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    const params = new URLSearchParams(window.location.search)

    if (params.get('layout')) {
      this.setLayout(params.get('layout'))
    }
  }

  setupListeners () {
    super.setupListeners()

    this.els.button.elements.forEach(btn => {
      this.listeners.add(btn, 'click', e => {
        this.handleLayoutButtonClick(e)
      })
    })
  }

  /**
   * @param {MouseEvent} e
   * @returns {void}
   */
  handleLayoutButtonClick (e) {
    const target = e.target.closest('[data-layout-switcher]')
    const layout = target.getAttribute('data-layout-switcher')
    this.setLayout(layout)
  }

  /**
   * @param {string} layout
   * @returns {void}
   * @public
   * */
  setLayout (layout) {
    this.els.button.elements.forEach(btn => {
      const btnLayout = btn.getAttribute('data-layout-switcher')
      btnLayout === layout
        ? btn.setAttribute('aria-pressed', 'true')
        : btn.setAttribute('aria-pressed', 'false')
    })
    this.itemList.setAttribute('data-layout', layout)
    this.modifyParams(layout)
  }

  /**
   * @param {string} layout
   * @returns {void}
   * @public
   * */
  modifyParams (layout) {
    const params = new URLSearchParams(window.location.search)
    params.set('layout', layout)
    window.history.replaceState({}, '', `${window.location.pathname}?${params}`)
  }

  get itemList () {
    return getElement(
      '[data-collection-filters-form-external-el="item-container"]',
    )
  }
}

if (!customElements.get('layout-switcher')) {
  customElements.define('layout-switcher', LayoutSwitcher)
}
